import {readReferral} from '@/lib/referrals';
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Loader2, Camera, Music, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { EVENT } from "@/data/event";

export default function CheckoutDialog({ open, onOpenChange, config }) {
  const [seats, setSeats] = useState(1);
  const [attendees, setAttendees] = useState(["", "", ""]);
  const [songs, setSongs] = useState([{ title: "", artist: "", platform_or_link: "" }]);
  const [addonQty, setAddonQty] = useState({});
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [smsConsent, setSmsConsent] = useState(false);
  const [releaseAgreed, setReleaseAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const { items, total } = useMemo(() => {
    if (!config) return { items: [], total: 0 };
    let t = (config.current_price || 0) * seats;
    const it = [];
    Object.entries(addonQty).forEach(([k, q]) => {
      if (q > 0) {
        const a = config.addons[k];
        const qty = a.per_unit ? q : 1;
        t += a.price * qty;
        it.push({ type: "addon", key: k, quantity: qty });
      }
    });
    return { items: it, total: t };
  }, [addonQty, config, seats]);

  if (!config) return null;
  const { tiers, tier_order, current_tier, current_price, addons } = config;
  const soldOut = !current_tier;
  const maxSeats = Math.min(4, config.remaining || 1);

  const setA = (k, d) => setAddonQty((p) => ({ ...p, [k]: Math.max(0, (p[k] || 0) + d) }));
  const setSong = (index, key, value) => {
    setSongs((current) => current.map((song, i) => i === index ? { ...song, [key]: value } : song));
  };
  const addSong = () => {
    if (songs.length < 3) setSongs((current) => [...current, { title: "", artist: "", platform_or_link: "" }]);
  };
  const removeSong = (index) => {
    setSongs((current) => current.length === 1
      ? [{ title: "", artist: "", platform_or_link: "" }]
      : current.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (!customer.name || !customer.email) {
      toast.error("Add your name and email to reserve your spot.");
      return;
    }
    if (seats > 1 && attendees.slice(0, seats - 1).some((a) => !a.trim())) {
      toast.error("Add a name for each guest in your team.");
      return;
    }
    const submittedSongs = songs.filter((song) =>
      song.title.trim() || song.artist.trim() || song.platform_or_link.trim()
    );
    if (submittedSongs.some((song) =>
      !song.title.trim() || !song.artist.trim() || !song.platform_or_link.trim()
    )) {
      toast.error("Complete the title, artist, and platform or link for each song.");
      return;
    }
    if (!releaseAgreed) {
      toast.error("Please agree to the photography / model-release to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        customer,
        tier: current_tier,
        seats,
        attendees: attendees.slice(0, seats - 1).filter((a) => a.trim()),
        songs: submittedSongs,
        sms_consent: smsConsent,
        release_agreed: releaseAgreed,
        items,
        origin_url: window.location.origin,
        ref: localStorage.getItem("pp_ref") || "direct",
        affiliate_ref: readReferral()?.event_path===window.location.pathname?readReferral()?.code:undefined,
      });
      sessionStorage.setItem("fotg_checkout_url", data.checkout_url);
      window.location.assign("/checkout/start");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="checkout-dialog"
        className="max-w-2xl bg-[#0d0d0d] border-white/10 text-[#F5F0E8] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-accent text-3xl tracking-wide">
            RESERVE YOUR SPOT <span className="neon-green">·</span> ONLY {config.remaining} LEFT
          </DialogTitle>
          <DialogDescription className="text-[#F5F0E8]/50">
            Come solo or bring your krewe, add up to three songs, and choose your photo add-ons.
          </DialogDescription>
        </DialogHeader>

        {/* Admission — price set by availability */}
        <div>
          <p className="font-accent text-sm text-[#F5F0E8]/50 mb-3">1 — ADMISSION</p>
          <div className="border border-[#39E75F]/40 bg-[#39E75F]/5 rounded-md p-4 flex items-center justify-between">
            <div>
              <div className="font-accent text-xl leading-none neon-green" data-testid="checkout-current-tier">
                {soldOut ? "SOLD OUT" : tiers[current_tier].name}
              </div>
              <div className="text-xs text-[#F5F0E8]/50 mt-1">
                {soldOut ? "No spots remaining." : `${tiers[current_tier].remaining} left at this price`}
              </div>
            </div>
            <div className="font-display text-4xl" data-testid="checkout-current-price">
              {soldOut ? "—" : `$${current_price}`}
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            {tier_order.map((k) => {
              const v = tiers[k];
              const isCurrent = k === current_tier;
              return (
                <div key={k}
                  className={`flex-1 text-center py-1.5 rounded text-xs border ${
                    v.sold_out ? "border-white/5 text-[#F5F0E8]/25 line-through"
                    : isCurrent ? "border-[#39E75F] text-[#39E75F]"
                    : "border-white/10 text-[#F5F0E8]/50"
                  }`}>
                  {v.name} ${v.price}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#F5F0E8]/40 mt-2">Price advances by availability. Includes the full 2-hour experience + two physical Polaroid prints.</p>
        </div>

        {/* Group booking */}
        {!soldOut && (
          <div>
            <p className="font-accent text-sm text-[#F5F0E8]/50 mb-3 mt-2">2 — HOW MANY SPOTS?</p>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((n) => {
                const disabled = n > maxSeats;
                return (
                  <button key={n} data-testid={`seats-${n}`} disabled={disabled}
                    onClick={() => setSeats(n)}
                    className={`flex-1 py-3 rounded-md border glow-hover text-center disabled:opacity-30 ${
                      seats === n ? "border-[#175CFF] bg-[#175CFF]/10" : "border-white/10 hover:border-white/30"
                    }`}>
                    <div className="font-display text-2xl leading-none">{n}</div>
                    <div className="font-accent tracking-widest text-[10px] text-[#F5F0E8]/50 mt-1">
                      {n === 4 ? "FULL KREWE" : n === 3 ? "TRIO" : n === 2 ? "DUO" : "SOLO"}
                    </div>
                  </button>
                );
              })}
            </div>
            {seats > 1 && (
              <div className="mt-3 grid gap-2">
                <p className="text-xs text-[#F5F0E8]/40">Krewe member names — {current_price ? "$" + current_price : ""} per person at the current price.</p>
                {Array.from({ length: seats - 1 }).map((_, i) => (
                  <Input key={i} data-testid={`attendee-${i}`} placeholder={`Krewe member ${i + 2} name`}
                    value={attendees[i] || ""}
                    onChange={(e) => { const a = [...attendees]; a[i] = e.target.value; setAttendees(a); }}
                    className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 px-0" />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Music */}
        <div>
          <div className="flex items-end justify-between gap-3 mb-3 mt-2">
            <div>
              <p className="font-accent text-sm text-[#F5F0E8]/50">3 — WHAT'S YOUR KREWE FEELING?</p>
              <p className="text-xs text-[#F5F0E8]/40 mt-1">Submit up to three songs you would love to hear.</p>
            </div>
            {songs.length < 3 && (
              <button type="button" data-testid="add-song" onClick={addSong}
                className="text-xs font-accent tracking-widest text-[#39E75F] hover:text-[#5cff7e] flex items-center gap-1">
                <Plus size={13} /> ADD SONG
              </button>
            )}
          </div>
          <div className="space-y-3">
            {songs.map((song, index) => (
              <div key={index} className="border border-white/10 rounded-md p-3 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-accent text-xs tracking-widest text-[#175CFF] flex items-center gap-2">
                    <Music size={13} /> SONG {index + 1}
                  </span>
                  {(songs.length > 1 || song.title || song.artist || song.platform_or_link) && (
                    <button type="button" aria-label={`Remove song ${index + 1}`} onClick={() => removeSong(index)}
                      className="text-[#F5F0E8]/40 hover:text-[#E32636]">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <Input data-testid={`song-title-${index}`} placeholder="Song title" value={song.title}
                    onChange={(e) => setSong(index, "title", e.target.value)}
                    className="bg-transparent border-white/15" />
                  <Input data-testid={`song-artist-${index}`} placeholder="Artist" value={song.artist}
                    onChange={(e) => setSong(index, "artist", e.target.value)}
                    className="bg-transparent border-white/15" />
                  <Input data-testid={`song-platform-${index}`} placeholder="Platform or link" value={song.platform_or_link}
                    onChange={(e) => setSong(index, "platform_or_link", e.target.value)}
                    className="bg-transparent border-white/15" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add-ons */}
        <div>
          <p className="font-accent text-sm text-[#F5F0E8]/50 mb-3 mt-2">4 — TAKE THE MOMENT HOME</p>
          <p className="text-xs text-[#F5F0E8]/40 -mt-2 mb-3">Choose a poster now and select your favorite event photo later.</p>
          <div className="space-y-2">
            {Object.entries(addons).map(([k, a]) => (
              <div key={k} className="flex items-center justify-between border-b border-white/5 pb-2">
                <div>
                  <div className="text-sm font-medium">{a.name} · <span className="neon-blue">${a.price}</span></div>
                  <div className="text-xs text-[#F5F0E8]/40">{a.blurb}</div>
                </div>
                <Stepper testid={`addon-${k}`} qty={addonQty[k] || 0} onChange={(d) => setA(k, d)} />
              </div>
            ))}
          </div>
        </div>

        {/* Customer */}
        <div>
          <p className="font-accent text-sm text-[#F5F0E8]/50 mb-3 mt-2">5 — YOUR DETAILS</p>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs text-[#F5F0E8]/50">Full name</Label>
              <Input data-testid="checkout-name" value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 px-0" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#F5F0E8]/50">Email</Label>
                <Input data-testid="checkout-email" type="email" value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 px-0" />
              </div>
              <div>
                <Label className="text-xs text-[#F5F0E8]/50">Phone (SMS)</Label>
                <Input data-testid="checkout-phone" value={customer.phone}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 px-0" />
              </div>
            </div>
          </div>
        </div>

        {/* Consent + policies */}
        <div className="space-y-3 border-t border-white/10 pt-4">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="release-consent-label">
            <input type="checkbox" data-testid="release-consent" checked={releaseAgreed}
              onChange={(e) => setReleaseAgreed(e.target.checked)}
              className="mt-1 accent-[#39E75F] w-4 h-4" />
            <span className="text-xs text-[#F5F0E8]/70 leading-relaxed">
              <span className="text-[#F5F0E8] font-medium">Photography / model release (required):</span> {EVENT.policies.release}
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="sms-consent-label">
            <input type="checkbox" data-testid="sms-consent" checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
              className="mt-1 accent-[#175CFF] w-4 h-4" />
            <span className="text-xs text-[#F5F0E8]/70 leading-relaxed">
              Text me my confirmation and a day-of reminder (optional). Message &amp; data rates may apply.
            </span>
          </label>
          <p className="text-[11px] text-[#F5F0E8]/40 leading-relaxed">{EVENT.policies.refund}</p>
        </div>

        <div className="sticky bottom-0 bg-[#0d0d0d] pt-3 border-t border-white/10">
          <button
            data-testid="checkout-submit"
            onClick={submit}
            disabled={loading || soldOut || !releaseAgreed}
            className="w-full py-4 bg-[#E32636] text-[#F5F0E8] font-accent text-xl tracking-wider rounded-md glow-hover hover:bg-[#ff2f40] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : <Camera size={20} />}
            {soldOut ? "SOLD OUT" : loading ? "REDIRECTING…" : `PAY $${total.toFixed(2)} — SECURE CHECKOUT`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stepper({ qty, onChange, testid }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <button data-testid={`${testid}-minus`} onClick={() => onChange(-1)}
        className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center hover:border-[#E32636] glow-hover">
        <Minus size={13} />
      </button>
      <span data-testid={`${testid}-qty`} className="w-5 text-center text-sm">{qty}</span>
      <button data-testid={`${testid}-plus`} onClick={() => onChange(1)}
        className="w-7 h-7 rounded-full border border-white/15 flex items-center justify-center hover:border-[#39E75F] glow-hover">
        <Plus size={13} />
      </button>
    </div>
  );
}

